import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { ArrowLeft, Shield, UserPlus, Search, Trash2 } from 'lucide-react';
import api from '../lib/api';

export default function AdminAdminsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchEmail, setSearchEmail] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedRole, setSelectedRole] = useState('admin');

  useEffect(() => {
    if (user?.admin_role !== 'super_admin') {
      navigate('/admin');
      return;
    }
    fetchAdmins();
  }, [user, navigate]);

  const fetchAdmins = async () => {
    try {
      const response = await api.get('/admin/admins');
      setAdmins(response.data.admins);
    } catch (error) {
      console.error('Error fetching admins:', error);
    } finally {
      setLoading(false);
    }
  };

  const searchUsers = async () => {
    if (!searchEmail) return;
    try {
      const response = await api.get(`/admin/users?search=${searchEmail}`);
      setSearchResults(response.data.users.filter(u => !u.admin_role));
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  const addAdmin = async (userId) => {
    try {
      await api.post(`/admin/roles/${userId}`, { role: selectedRole });
      setShowAddModal(false);
      setSearchEmail('');
      setSearchResults([]);
      fetchAdmins();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur');
    }
  };

  const removeAdmin = async (userId) => {
    if (!confirm('Retirer les droits admin de cet utilisateur ?')) return;
    try {
      await api.post(`/admin/roles/${userId}`, { role: null });
      fetchAdmins();
    } catch (error) {
      alert(error.response?.data?.detail || 'Erreur');
    }
  };

  const getRoleColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-red-100 text-red-700';
      case 'admin': return 'bg-blue-100 text-blue-700';
      case 'support': return 'bg-green-100 text-green-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'super_admin': return 'Super Admin';
      case 'admin': return 'Admin';
      case 'support': return 'Support';
      default: return role;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="bg-card border-b border-border sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate('/admin')}>
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="text-xl font-bold">Administrateurs</h1>
          </div>
          <Button onClick={() => setShowAddModal(true)}>
            <UserPlus className="w-4 h-4 mr-2" />
            Ajouter Admin
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full mx-auto" />
          </div>
        ) : (
          <div className="space-y-4">
            {admins.map((admin) => (
              <Card key={admin.user_id}>
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{admin.name}</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${getRoleColor(admin.admin_role)}`}>
                          {getRoleLabel(admin.admin_role)}
                        </span>
                      </div>
                      <p className="text-sm text-muted-foreground">{admin.email}</p>
                    </div>
                  </div>
                  {admin.user_id !== user.user_id && admin.admin_role !== 'super_admin' && (
                    <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeAdmin(admin.user_id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Ajouter un Administrateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="text-sm font-medium">Rechercher un utilisateur</label>
              <div className="flex gap-2 mt-2">
                <Input
                  value={searchEmail}
                  onChange={(e) => setSearchEmail(e.target.value)}
                  placeholder="Email ou nom..."
                />
                <Button onClick={searchUsers}>
                  <Search className="w-4 h-4" />
                </Button>
              </div>
            </div>
            
            <div>
              <label className="text-sm font-medium">Rôle</label>
              <div className="flex gap-2 mt-2">
                {['admin', 'support'].map((role) => (
                  <Button
                    key={role}
                    variant={selectedRole === role ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setSelectedRole(role)}
                  >
                    {getRoleLabel(role)}
                  </Button>
                ))}
              </div>
            </div>

            {searchResults.length > 0 && (
              <div className="border rounded-lg divide-y max-h-[200px] overflow-y-auto">
                {searchResults.map((u) => (
                  <div key={u.user_id} className="p-3 flex items-center justify-between hover:bg-muted/50">
                    <div>
                      <p className="font-medium">{u.name}</p>
                      <p className="text-sm text-muted-foreground">{u.email}</p>
                    </div>
                    <Button size="sm" onClick={() => addAdmin(u.user_id)}>Ajouter</Button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddModal(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
